import type { DeviceInventorySelfReportOutput } from '@/lib/api/orpc/contracts/it-operation-core-device-inventory';

/**
 * セルフレポートの申告内容。
 *
 * 🔴 **出所は oRPC contract であって `schema.d.ts`（旧 REST）ではない。**
 * 以前は `ApiResponseBody<'/v2/it-operation-core/device-inventory/self-reports','get'>`
 * から引いていたが、この REST は BE で撤去済みで `schema.d.ts` は再生成されない。
 * 追従が保証されないため、oRPC contract 由来の型を使う。
 *
 * 同じ理由で `DeviceInventoryOperation/query/type.ts` も contract 由来に切り替えてある。
 */
type SelfReportDataFromApi = NonNullable<DeviceInventorySelfReportOutput['selfReport']>;
export type PlacementVerificationStatus = SelfReportDataFromApi['reportResult'];
export type VerificationMethod = SelfReportDataFromApi['verificationMethod'];
export type DeviceCondition = NonNullable<SelfReportDataFromApi['deviceCondition']>;
export type DeviceStatus = DeviceCondition | 'UNREPORTED';
export type ReportStatus = 'REPORTED' | 'NOT_REPORTED';
export const VERIFICATION_METHOD = {
  PHYSICAL_CHECK: 'PHYSICAL_CHECK',
  SCAN: 'SCAN',
  PHOTO_RECORD: 'PHOTO_RECORD',
  ASSET_TAG: 'ASSET_TAG',
  INFORMAL: 'INFORMAL',
  REPRESENTATIVE_BY_STAFF: 'REPRESENTATIVE_BY_STAFF'
} as const satisfies Record<string, VerificationMethod>;

/**
 * 個人画面で「対象デバイスを照合した」と言える確認方法。
 *
 * 照合とは、報告の対象がまさに手元の個体であることをその場で特定した事実を指す。
 * 個人画面から到達しうるのは `SCAN`（QR 読み取り）/ `ASSET_TAG`（管理IDの手入力）/
 * `INFORMAL`（「はい」と答えただけ）の 3 つで、照合を伴うのは前 2 つだけ。
 *
 * BE 側の対（`device-inventory-report.entity.ts` の `UNVERIFIED_VERIFICATION_METHODS`）は
 * **未照合の側を列挙する**。あちらはスタッフ代理報告（`REPRESENTATIVE_BY_STAFF`）も
 * 通す必要があり、こちらは個人画面に閉じているため列挙の向きが逆になる。
 * 担保の SSOT は BE。ここは押せないようにするための UX ガード。
 */
export const VERIFIED_VERIFICATION_METHODS = [VERIFICATION_METHOD.SCAN, VERIFICATION_METHOD.ASSET_TAG] as const satisfies readonly VerificationMethod[];
export const PLACEMENT_VERIFICATION_STATUS = {
  OWNED: 'OWNED',
  LOST: 'LOST',
  NOT_KNOWN: 'NOT_KNOWN',
  RETURNED: 'RETURNED',
  OTHER: 'OTHER'
} as const satisfies Record<string, PlacementVerificationStatus>;
export const DEVICE_CONDITION = {
  GOOD: 'GOOD',
  BAD: 'BAD'
} as const satisfies Record<string, DeviceCondition>;
export type DeviceInventoryFormGeneration = 'v1' | 'v2';

/**
 * デバイス棚卸し回答フォームの世代。BE の `SelfReportFormShape`（v1/v2）と対。
 * ⚠️ BE の `self-report-form-shape.ts` が SSOT。片方だけ変えると初期値が入らなくなる。
 */
export const DEVICE_INVENTORY_FIELD_IDS = {
  v1: {
    DEVICE_ID: 'DeviceIDInformation.0.DeviceIDInformation.0.DeviceID',
    DEVICE_TAG: 'DeviceIDInformation.0.DeviceIDInformation.0.DeviceTag',
    VERIFICATION_METHOD: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.VerificationMethod',
    PLACEMENT_VERIFICATION_STATUS: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.PlacementVerificationStatus',
    INVENTORY_DEVICE_CONDITION: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryDeviceCondition',
    INVENTORY_REPORTER: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryReporter',
    INVENTORY_MEMO: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryMemo'
  },
  v2: {
    DEVICE_ID: 'DeviceIDInformation.0.DeviceIDInformation.0.DeviceID',
    DEVICE_TAG: 'DeviceIDInformation.0.DeviceIDInformation.0.DeviceTag',
    VERIFICATION_METHOD: 'DeviceInventory.0.DeviceInventory.0.DeviceInventoryVerificationMethod',
    PLACEMENT_VERIFICATION_STATUS: 'DeviceInventory.0.DeviceInventory.0.DeviceInventoryReportResult',
    INVENTORY_DEVICE_CONDITION: 'DeviceInventory.0.DeviceInventory.0.DeviceInventoryDeviceCondition',
    INVENTORY_REPORTER: 'DeviceInventory.0.DeviceInventory.0.DeviceInventoryReporter',
    INVENTORY_MEMO: 'DeviceInventory.0.DeviceInventory.0.DeviceInventoryMemo'
  },
  // v1 のフィールド ID をトップレベルにも残す暫定の橋渡し。
  // 世代分岐に未追従の利用側（個人アプリのフォーム / 一覧の query）が
  // フラット参照のままのため、それらが v1/v2 を明示する形へ移るまで維持する。
  // 後続 PR で全利用側が移行した時点で削除する。
  DEVICE_ID: 'DeviceIDInformation.0.DeviceIDInformation.0.DeviceID',
  DEVICE_TAG: 'DeviceIDInformation.0.DeviceIDInformation.0.DeviceTag',
  VERIFICATION_METHOD: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.VerificationMethod',
  PLACEMENT_VERIFICATION_STATUS: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.PlacementVerificationStatus',
  INVENTORY_DEVICE_CONDITION: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryDeviceCondition',
  INVENTORY_REPORTER: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryReporter',
  INVENTORY_MEMO: 'DeviceUseAndPlacement.0.DevicePlacementStatus.0.InventoryMemo'
} as const;

/**
 * デバイス棚卸しフォームの属性ID（= BE の AttributeDefinitionCode）。
 *
 * `DEVICE_INVENTORY_FIELD_IDS` はコレクション・グループを含むフラットキーだが、
 * フォーム定義を属性単位で触るとき（例: 個人アプリのフォーム射影）に必要なのは
 * 属性IDだけなので分けて置く。フラットキーの末尾と一致する。
 */
export const DEVICE_INVENTORY_ATTRIBUTE_IDS = {
  v1: {
    DEVICE_ID: 'DeviceID',
    DEVICE_TAG: 'DeviceTag',
    DEVICE_QR_CODE: 'DeviceQrCode',
    VERIFICATION_METHOD: 'VerificationMethod',
    PLACEMENT_VERIFICATION_STATUS: 'PlacementVerificationStatus',
    INVENTORY_DEVICE_CONDITION: 'InventoryDeviceCondition',
    INVENTORY_REPORTER: 'InventoryReporter',
    INVENTORY_MEMO: 'InventoryMemo'
  },
  v2: {
    DEVICE_ID: 'DeviceID',
    DEVICE_TAG: 'DeviceTag',
    DEVICE_QR_CODE: 'DeviceQrCode',
    VERIFICATION_METHOD: 'DeviceInventoryVerificationMethod',
    PLACEMENT_VERIFICATION_STATUS: 'DeviceInventoryReportResult',
    INVENTORY_DEVICE_CONDITION: 'DeviceInventoryDeviceCondition',
    INVENTORY_REPORTER: 'DeviceInventoryReporter',
    INVENTORY_MEMO: 'DeviceInventoryMemo'
  }
} as const;

/** v2 の中分類コード。これを持つフォーム定義は v2（BE detectSelfReportFormShape と同じ判定軸） */
const V2_MEDIUM_CATEGORY_ID = 'DeviceInventory';

/**
 * フォーム定義から回答フォームの世代を検出する。
 * v1/v2 で中分類が重複しない（v1: DeviceUseAndPlacement / v2: DeviceInventory）ため一意。
 */
export function detectDeviceInventoryFormGeneration(formDefinition: {
  form?: {
    attributeCollections?: readonly {
      id?: string;
    }[] | null;
  } | null;
}): DeviceInventoryFormGeneration {
  const hasV2 = formDefinition.form?.attributeCollections?.some(c => c.id === V2_MEDIUM_CATEGORY_ID);
  return hasV2 ? 'v2' : 'v1';
}